import { Link } from "react-router-dom";
import { useState } from "react";
import { PageHeader, CTASection, Disclaimer } from "@/components/Shared";
import {
  Plane, Heart, Briefcase, Users, ShieldCheck, CheckCircle2, XCircle, FileCheck,
  Clock, AlertTriangle, ArrowRight, ChevronDown, ExternalLink
} from "lucide-react";

const STREAMS = [
  {
    icon: Heart,
    title: "Tourism & Holiday",
    desc: "Sightseeing, leisure travel, exploring Australia — Sydney, Melbourne, Perth, Great Barrier Reef, Tasmania and beyond.",
  },
  {
    icon: Users,
    title: "Visiting Family or Friends",
    desc: "Spend quality time with loved ones living in Australia. Sponsorship from an Australian relative can strengthen your application.",
  },
  {
    icon: Briefcase,
    title: "Business Visitor",
    desc: "Attend conferences, meetings, contract negotiations or exploratory business activities. Paid work for an Australian employer is not permitted.",
  },
  {
    icon: Plane,
    title: "Medical Treatment",
    desc: "Receive specialist treatment or consultation in Australia. Supporting medical documentation and funding evidence required.",
  },
];

const CAN_DO = [
  "Stay in Australia for up to 3, 6 or 12 months per entry (period decided at visa grant)",
  "Travel in and out of Australia multiple times within the visa's validity period",
  "Engage in tourism, sightseeing and personal recreation activities",
  "Visit family members and friends across Australia",
  "Undertake short, recreational study of up to 3 months",
  "Attend short business meetings, conferences and exploratory negotiations",
];

const CANNOT_DO = [
  "Work for an Australian employer (paid or unpaid) on the Tourist stream",
  "Provide goods or services to the Australian community",
  "Stay beyond the period granted on your visa",
  "Apply for most other visa types while in Australia if condition 8503 (No Further Stay) is attached",
  "Engage in formal study for more than 3 months",
];

const REQUIREMENTS = [
  "Be a genuine visitor — clear intention to leave Australia at the end of your stay",
  "Hold sufficient funds for the duration of your visit and return travel",
  "Demonstrate strong ties to your home country (work, family, property, study)",
  "Meet health requirements (PIC 4007)",
  "Meet character requirements (PIC 4001)",
  "Hold no outstanding public health debts to the Commonwealth",
  "Hold adequate health insurance (recommended, especially for older applicants)",
];

const DOCUMENTS = [
  "Valid passport — sufficient validity beyond intended stay",
  "Recent passport photograph",
  "Detailed travel itinerary — flights, accommodation, activities",
  "Return ticket or onward travel arrangement",
  "Bank statements covering the past 3–6 months",
  "Evidence of financial capacity (savings, fixed deposits, employer letter)",
  "Employment letter and approved leave (or proof of business / study)",
  "Invitation letter from Australian family or sponsor (where applicable)",
  "Previous travel history and visa decisions (full disclosure)",
  "Health insurance details (recommended)",
];

const REFUSALS = [
  { title: "Insufficient funds", desc: "Bank statements do not show enough money to support the visit and return travel." },
  { title: "Weak home country ties", desc: "Inadequate evidence of employment, family, study or property anchoring you abroad." },
  { title: "Travel history concerns", desc: "Inconsistent or undisclosed prior travel — or earlier visa refusals not addressed in the application." },
  { title: "Genuine visitor doubts", desc: "The Department forms a view that the real intent may not be a short visit." },
  { title: "Incomplete documentation", desc: "Missing supporting documents — invitation letter, employer letter, itinerary or photographs." },
  { title: "Health or character issues", desc: "Outstanding debts, character concerns or unresolved health matters." },
];

const FAQS = [
  {
    q: "How long does a Visitor Visa (Subclass 600) processing usually take?",
    a: "Most Tourist Stream applications from outside Australia are processed within a few weeks. Sponsored Family Stream and complex cases can take longer due to sponsor checks. Always check current processing times on immi.homeaffairs.gov.au and apply well ahead of intended travel — we recommend 6–8 weeks before departure.",
  },
  {
    q: "Can I extend my Visitor Visa while in Australia?",
    a: "You may be able to apply for a new Visitor Visa from inside Australia — provided your current visa does not carry condition 8503 (No Further Stay). The new application is assessed afresh; you must demonstrate ongoing genuine visitor intent and sufficient funds.",
  },
  {
    q: "Can I work on a Visitor Visa?",
    a: "No. Condition 8101 (No Work) prohibits paid work for the Australian community. Limited unpaid voluntary work in narrow circumstances may be permissible — please speak with us first.",
  },
  {
    q: "Can my partner or children be included on the same application?",
    a: "Each applicant generally lodges their own application (linked as a 'family group') and pays their own visa application charge. We help structure the family lodgement so the underlying genuine-visitor narrative is consistent across applications.",
  },
  {
    q: "Do I need a sponsor for the Visitor Visa?",
    a: "No — most visitors apply on the Tourist Stream without a sponsor. However, certain applicants from higher-risk profiles or with weak documentation may strengthen the application via the Sponsored Family Stream, which requires an eligible Australian sponsor (and may involve a security bond).",
  },
  {
    q: "What happens if my Visitor Visa is refused?",
    a: "A refusal can affect future visa applications, so it must be handled carefully. We can review the refusal letter, identify the deficiencies and advise on a fresh application or, where appropriate, a review pathway. Book a consultation to discuss your circumstances.",
  },
  {
    q: "How much does the Visitor Visa cost?",
    a: "Department of Home Affairs visa application charges are set by the government and may change. Please refer to the current pricing on immi.homeaffairs.gov.au and our service charges page for our professional fee.",
  },
];

function FaqItem({ q, a, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div data-testid="visitor-faq-item" className="border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start justify-between gap-4 p-5 text-left hover:bg-[#FCFBFD] transition-colors"
        aria-expanded={open}
      >
        <span className="font-display text-base md:text-lg text-[#29324A] leading-snug">{q}</span>
        <ChevronDown className={`h-5 w-5 text-[#29324A] shrink-0 mt-1 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-5 pb-5 text-sm text-slate-600 leading-relaxed border-t border-slate-100">
          {a}
        </div>
      )}
    </div>
  );
}

export default function VisitorVisa600() {
  return (
    <div>
      <PageHeader
        eyebrow="Subclass 600 · Tourist · Sponsored Family · Business"
        title="Visitor Visa (Subclass 600)."
        subtitle="Short-stay visits to Australia for tourism, visiting family or friends, business or medical reasons — prepared and lodged by our team of Registered Migration Agents."
        image="/assets/visitor.jpg"
      />

      {/* Overview */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-slate-600 leading-relaxed text-[15px] space-y-5">
          <p>
            The <strong className="text-[#29324A]">Visitor Visa (Subclass 600)</strong> is Australia's primary visitor visa for travellers seeking a short stay — typically up to 3, 6 or 12 months — for tourism, visiting family or friends, business activities (no paid work for Australian employers), or medical treatment.
          </p>
          <p>
            The visa is granted in different streams depending on your purpose of travel. While the application is lodged online and looks straightforward, the Department of Home Affairs assesses each applicant against a "genuine visitor" requirement — which is where most refusals occur. Strong documentation, financial evidence and home-country ties are critical.
          </p>
          <p>
            Our team can help you choose the right stream, prepare a complete file and explain your circumstances clearly to maximise the chance of a smooth grant.
          </p>
        </div>
      </section>

      {/* Visit purposes */}
      <section data-testid="visitor-purposes" className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" /> Visit Purposes
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-10">
            What you can come to Australia for
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-slate-200">
            {STREAMS.map((s, i) => (
              <div key={i} className="bg-white p-6">
                <s.icon className="h-7 w-7 text-[#C43A89] mb-4" />
                <h3 className="font-display text-lg text-[#29324A] mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Can / Cannot do */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8">
          <div className="bg-[#F8F4FB] p-7 border-l-4 border-[#29324A]">
            <CheckCircle2 className="h-7 w-7 text-[#29324A] mb-3" />
            <h3 className="font-display text-2xl text-[#29324A] mb-4">What you can do</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              {CAN_DO.map((c, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <CheckCircle2 className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-7 border-l-4 border-[#C43A89]">
            <XCircle className="h-7 w-7 text-[#C43A89] mb-3" />
            <h3 className="font-display text-2xl text-[#29324A] mb-4">What you cannot do</h3>
            <ul className="space-y-2 text-sm text-slate-600">
              {CANNOT_DO.map((c, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <XCircle className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Requirements + Documents */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
              <span className="brand-dot bg-[#C43A89]" /> Basic Requirements
            </div>
            <h3 className="font-display text-3xl text-[#29324A] leading-tight mb-6">
              What you need to meet
            </h3>
            <ul className="space-y-3 text-sm text-slate-700">
              {REQUIREMENTS.map((r, i) => (
                <li key={i} className="flex items-start gap-3 bg-white p-4 border-l-2 border-[#29324A]">
                  <ShieldCheck className="h-4 w-4 text-[#29324A] mt-0.5 shrink-0" />
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
              <span className="brand-dot bg-[#C43A89]" /> Documents
            </div>
            <h3 className="font-display text-3xl text-[#29324A] leading-tight mb-6">
              Documents we'll typically prepare
            </h3>
            <ul className="space-y-2 text-sm text-slate-600 bg-white p-5 border border-slate-200">
              {DOCUMENTS.map((d, i) => (
                <li key={i} className="flex items-start gap-2.5">
                  <FileCheck className="h-4 w-4 text-[#C43A89] mt-0.5 shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-slate-500 mt-3 italic">
              Document needs vary per applicant. We provide a tailored checklist after our initial assessment.
            </p>
          </div>
        </div>
      </section>

      {/* Extending stay */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 bg-[#29324A] grain text-white p-8 md:p-10 relative overflow-hidden">
          <span aria-hidden className="absolute top-0 left-0 h-1 w-20 bg-[#C43A89]" />
          <div className="text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-2">
            <Clock className="h-3.5 w-3.5" /> Extending Your Stay
          </div>
          <h3 className="font-display text-2xl md:text-3xl text-white leading-tight mb-4">
            Already in Australia? You may be able to extend.
          </h3>
          <p className="text-sm md:text-base text-slate-300 leading-relaxed">
            If your existing Visitor Visa allows further applications (no condition 8503 attached), you may lodge a new Visitor Visa application from inside Australia before your current visa expires. The new application is assessed afresh, so a clear genuine-visitor narrative, updated financial evidence and clean character history are still essential.
          </p>
        </div>
      </section>

      {/* Processing time */}
      <section className="py-14 md:py-20 bg-[#F8F4FB]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-px bg-slate-200">
          <div className="bg-white p-6">
            <Clock className="h-7 w-7 text-[#29324A] mb-3" />
            <h4 className="font-display text-lg text-[#29324A] mb-2">Tourist Stream (offshore)</h4>
            <p className="text-sm text-slate-600 leading-relaxed">Most decisions within a few weeks. Apply 6–8 weeks before intended travel.</p>
          </div>
          <div className="bg-white p-6">
            <Clock className="h-7 w-7 text-[#29324A] mb-3" />
            <h4 className="font-display text-lg text-[#29324A] mb-2">Sponsored Family Stream</h4>
            <p className="text-sm text-slate-600 leading-relaxed">Generally slower — sponsor checks and possible security bond add to processing time.</p>
          </div>
          <div className="bg-white p-6">
            <Clock className="h-7 w-7 text-[#29324A] mb-3" />
            <h4 className="font-display text-lg text-[#29324A] mb-2">Business Visitor Stream</h4>
            <p className="text-sm text-slate-600 leading-relaxed">Typically a few weeks, often faster for established business applicants with clear evidence.</p>
          </div>
        </div>
        <p className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-slate-500 mt-4 italic">
          Indicative only. Latest published processing times: <a href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-processing-times" target="_blank" rel="noopener noreferrer" className="text-[#29324A] underline">immi.homeaffairs.gov.au visa processing times</a>.
        </p>
      </section>

      {/* Refusal reasons */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <AlertTriangle className="h-3.5 w-3.5 text-[#C43A89]" /> Common Refusal Reasons
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-10">
            Why Visitor Visa applications often fail.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {REFUSALS.map((r, i) => (
              <div key={i} className="bg-white border border-slate-200 p-5">
                <h4 className="font-display text-lg text-[#29324A] mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-[#C43A89]" /> {r.title}
                </h4>
                <p className="text-sm text-slate-600 leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-sm text-slate-500 mt-6">
            A Visitor Visa refusal can affect every future visa application — including student, partner and skilled. Engaging a Registered Migration Agent for higher-risk profiles is highly recommended.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section data-testid="visitor-faqs" className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-[11px] font-bold tracking-[0.22em] text-[#C43A89] uppercase mb-3 flex items-center gap-3">
            <span className="brand-dot bg-[#C43A89]" /> FAQs
          </div>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight mb-10">
            Frequently asked questions
          </h2>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <FaqItem key={i} q={f.q} a={f.a} defaultOpen={i === 0} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Booking */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Plane className="h-10 w-10 text-[#C43A89] mx-auto mb-5" />
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] leading-tight">
            Ready to plan your visit to Australia?
          </h2>
          <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">
            Book a confidential consultation with one of our Registered Migration Agents — we'll assess your profile, identify the right Visitor stream and walk you through documentation before lodgement.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link
              to="/book-consultation?service=visitor-600"
              data-testid="visitor-book-cta"
              className="inline-flex items-center justify-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white font-semibold py-3.5 px-7 transition-colors btn-shimmer text-xs uppercase tracking-wider"
            >
              Book a Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/service-charges"
              data-testid="visitor-pricing-cta"
              className="inline-flex items-center justify-center gap-2 border-2 border-[#29324A] text-[#29324A] hover:bg-[#29324A] hover:text-white font-semibold py-3.5 px-7 transition-colors text-xs uppercase tracking-wider"
            >
              View Service Charges
            </Link>
            <a
              href="https://immi.homeaffairs.gov.au/visas/getting-a-visa/visa-listing/visitor-600"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 text-[#29324A] hover:text-[#C43A89] font-semibold py-3.5 px-3 text-xs uppercase tracking-wider"
            >
              Department of Home Affairs <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Disclaimer>
            Information on this page is general in nature and does not replace personalised migration advice. Visitor Visa rules, processing times and Department charges can change at any time. Speak with one of our Registered Migration Agents for a tailored assessment of your circumstances.
          </Disclaimer>
        </div>
      </section>

      <CTASection title="Speak with a Registered Migration Agent before you apply." />
    </div>
  );
}
